import { Link } from 'react-router-dom';
import { Home, Mail, Phone, MapPin } from 'lucide-react';
import { gaEvents } from '@/hooks/useGoogleAnalytics';

interface Town {
  id: string;
  name: string;
  slug: string;
}

interface Area {
  id: string;
  name: string;
  slug: string;
  town_id?: string | null;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url?: string | null;
}

interface FooterProps {
  siteName?: string;
  locationServed?: string;
  towns?: Town[];
  areas?: Area[];
  realtor?: RealtorData | null;
  enableBuyers?: boolean;
  enableSellers?: boolean;
  enableMortgageInfo?: boolean;
}

export function Footer({ 
  siteName = 'Real Estate', 
  locationServed = '', 
  towns = [], 
  areas = [], 
  realtor,
  enableBuyers = true,
  enableSellers = true,
  enableMortgageInfo = true
}: FooterProps) {
  const realtorName = realtor ? [realtor.first_name, realtor.last_name].filter(Boolean).join(' ') : '';
  const realtorLocation = realtor ? [realtor.city, realtor.state].filter(Boolean).join(', ') : '';
  
  // Show areas in footer only when there's exactly 1 town
  const showAreasInFooter = towns.length === 1 && areas.length > 0;
  const singleTown = towns.length === 1 ? towns[0] : null;

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Realtor Info */}
          {realtor && realtorName && (
            <div className="md:col-span-2 lg:col-span-1">
              <div className="flex items-start gap-4 mb-4">
                {realtor.photo_url && (
                  <img
                    src={realtor.photo_url}
                    alt={realtorName}
                    className="w-16 h-16 rounded-full object-cover shrink-0 border-2 border-primary-foreground/20"
                  />
                )}
                <div>
                  <h4 className="font-display font-semibold text-lg">{realtorName}</h4>
                  {realtor.phone && (
                    <a 
                      href={`tel:${realtor.phone}`} 
                      className="text-primary-foreground/80 hover:text-primary-foreground text-sm flex items-center gap-1 mt-1"
                      onClick={() => gaEvents.clickCall(realtor.phone || undefined)}
                    >
                      <Phone className="h-3 w-3" />
                      {realtor.phone}
                    </a>
                  )}
                  {realtorLocation && (
                    <p className="text-primary-foreground/60 text-sm flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {realtorLocation}
                    </p>
                  )}
                </div>
              </div>
              {realtor.license_numbers && (
                <p className="text-xs text-primary-foreground/50">
                  License: {realtor.license_numbers}
                </p>
              )}
            </div>
          )}

          {/* Brand - show if no realtor */}
          {(!realtor || !realtorName) && (
            <div>
              <Link to="/" className="flex items-center gap-2 font-display text-xl font-semibold mb-4">
                <Home className="h-6 w-6" />
                {siteName}
              </Link>
              <p className="text-primary-foreground/70 font-body text-sm mb-4">
                Your trusted real estate partner in {locationServed || 'your area'}.
              </p>
              <div className="flex flex-col gap-2 text-sm text-primary-foreground/70">
                <a 
                  href="mailto:info@example.com" 
                  className="flex items-center gap-2 hover:text-primary-foreground transition-colors"
                  onClick={() => gaEvents.clickEmail('info@example.com')}
                >
                  <Mail className="h-4 w-4" />
                  Contact us
                </a>
              </div>
            </div>
          )}

          {/* Towns We Serve - show when multiple towns */}
          {towns.length > 1 && (
            <div className="lg:col-span-2">
              <h4 className="font-display text-lg font-semibold mb-4">Towns We Serve</h4>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {towns.map((town) => (
                  <li key={town.id}>
                    <Link
                      to={`/${town.slug}`}
                      className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body"
                    >
                      {town.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Neighborhoods - show when single town with areas */}
          {showAreasInFooter && singleTown && (
            <div className="lg:col-span-2">
              <h4 className="font-display text-lg font-semibold mb-4">Neighborhoods in {singleTown.name}</h4>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {areas.map((area) => (
                  <li key={area.id}>
                    <Link
                      to={`/${singleTown.slug}/${area.slug}`}
                      className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body"
                    >
                      {area.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                  Home
                </Link>
              </li>
              {towns.length > 1 && (
                <li>
                  <Link to="/towns" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                    All Towns
                  </Link>
                </li>
              )}
              {singleTown && (
                <li>
                  <Link to={`/${singleTown.slug}`} className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                    {singleTown.name}
                  </Link>
                </li>
              )}
              {enableBuyers && (
                <li>
                  <Link to="/buyers" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                    Buyers
                  </Link>
                </li>
              )}
              {enableSellers && (
                <li>
                  <Link to="/sellers" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                    Sellers
                  </Link>
                </li>
              )}
              {enableMortgageInfo && (
                <li>
                  <Link to="/mortgages" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                    Mortgage Info
                  </Link>
                </li>
              )}
              <li>
                <Link to="/admin/login" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors font-body">
                  Agent Login
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/50 font-body">
          © {new Date().getFullYear()} {siteName}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
